import React from 'react';

import { useStartCameraWebRTC } from 'src/features/cameras/api/startCameraWebRTC';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import { VideoSkeleton } from 'src/registry/ui/Skeleton.tsx';

type Props = {
    cameraId: number;
};

export default function WebRTCVideoPlayer(props: Props) {
    const { cameraId } = props;
    const { parkingId } = useParking();

    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const { mutate: startCameraWebRTC } = useStartCameraWebRTC();

    React.useEffect(() => {
        const playVideo = () => {
            const webrtc = new RTCPeerConnection({
                iceServers: [
                    {
                        urls: [
                            'stun:stun.l.google.com:19302',
                            'stun:stun1.l.google.com:19302',
                            'stun:stun2.l.google.com:19302',
                            'stun:stun3.l.google.com:19302',
                            'stun:stun4.l.google.com:19302',
                        ],
                    },
                ],
                // @ts-expect-error This is a valid option in Chromium https://webrtc.org/getting-started/unified-plan-transition-guide
                sdpSemantics: 'unified-plan',
            });

            webrtc.ontrack = function (event) {
                console.log(event.streams.length + ' track is delivered');
                if (videoRef.current) {
                    videoRef.current.srcObject = event.streams[0];
                    videoRef.current
                        .play()
                        .then(() => {
                            setLoading(false);
                        })
                        .catch((error) => {
                            console.warn('play error', error);
                        });
                }
            };
            webrtc.addTransceiver('video', { direction: 'sendrecv' });
            webrtc.onnegotiationneeded = async function handleNegotiationNeeded() {
                const offer = await webrtc.createOffer();

                await webrtc.setLocalDescription(offer);

                startCameraWebRTC(
                    {
                        cameraId,
                        parkingId,
                        data: btoa(webrtc.localDescription?.sdp || ''),
                    },
                    {
                        onSuccess: ({ data }) => {
                            try {
                                webrtc
                                    .setRemoteDescription(
                                        new RTCSessionDescription({ type: 'answer', sdp: atob(data) }),
                                    )
                                    .catch((reason) => {
                                        console.warn('Could not set webrtc remote description', reason);
                                    });
                            } catch (e) {
                                console.warn(e);
                            }
                        },
                    },
                );
            };

            const webrtcSendChannel = webrtc.createDataChannel('camera-webrtc-send');
            let webrtcSendChannelInterval: ReturnType<typeof setInterval>;
            webrtcSendChannel.onopen = () => {
                console.log(`${webrtcSendChannel.label} has opened`);

                webrtcSendChannel.send('ping');
                webrtcSendChannelInterval = setInterval(() => {
                    webrtcSendChannel.send('ping');
                }, 30 * 1000);
            };
            webrtcSendChannel.onclose = (_event) => {
                console.log(`${webrtcSendChannel.label} has closed`);

                stopVideo();
                playVideo();
            };
            webrtcSendChannel.onmessage = (event) => console.log(event.data);

            function stopVideo() {
                if (webrtcSendChannelInterval) {
                    clearInterval(webrtcSendChannelInterval);
                }

                webrtc.close();
                webrtcSendChannel.close();

                setLoading(true);

                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.srcObject = null;
                }
            }

            return stopVideo;
        };

        return playVideo();
    }, [cameraId, parkingId, startCameraWebRTC]);

    return (
        <div className={loading ? 'relative aspect-video w-full' : 'relative'}>
            <video ref={videoRef} autoPlay muted playsInline className="max-h-full max-w-full" />
            {loading && <VideoSkeleton className="absolute top-0 left-0 size-full" />}
        </div>
    );
}
